"use client";

import { useCallback, useState } from "react";

const STORAGE_KEY = "search-history";
const MAX_ITEMS = 8;

function read(): string[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
}

function persist(items: string[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        // localStorage indisponível (modo privado/quota) — ignora silenciosamente.
    }
}

/** Histórico de buscas do usuário (persistido em localStorage). */
export function useSearchHistory() {
    const [history, setHistory] = useState<string[]>(read);

    const add = useCallback((term: string) => {
        const trimmed = term.trim();
        if (!trimmed) return;
        setHistory((prev) => {
            const next = [trimmed, ...prev.filter((t) => t !== trimmed)].slice(0, MAX_ITEMS);
            persist(next);
            return next;
        });
    }, []);

    const remove = useCallback((term: string) => {
        setHistory((prev) => {
            const next = prev.filter((t) => t !== term);
            persist(next);
            return next;
        });
    }, []);

    // Relê o localStorage — necessário para consumidores que ficam montados
    // por muito tempo sem desmontar (ex.: SearchFullscreenModal, que só monta
    // na primeira abertura e nunca desmonta depois), onde o estado inicial de
    // `useState(read)` fica desatualizado em relação a buscas feitas alhures.
    // Referência estável (useCallback) — evita disparar em loop efeitos que a
    // tenham como dependência (setHistory sempre cria array novo, mesmo com
    // conteúdo igual, então uma referência instável de `refresh` recriaria o
    // efeito a cada render).
    const refresh = useCallback(() => setHistory(read()), []);

    return { history, add, remove, refresh };
}
